/**
 * VibeCoin API Client
 * Communicates with mkey worker VibeCoin endpoints
 */

const MKEY_API_URL = import.meta.env.VITE_MKEY_API_URL || 'https://mkey-api-production.fidelismadboat.workers.dev';

function getToken(): string | null {
  try {
    const stored = localStorage.getItem('madboat-auth');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.state?.token || null;
  } catch {
    return null;
  }
}

async function fetchVibeCoin<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`${MKEY_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const data = await res.json().catch(() => ({ success: false, error: 'Parse error' }));

  if (!res.ok || !data.success) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }

  return data.data;
}

// ── Types ──

export interface VibeCoinTransaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  source: string;
  description: string;
  balance_after: number;
  created_at: number;
}

export interface VibeCoinBalance {
  balance: number;
  recentTransactions: VibeCoinTransaction[];
}

export interface VibeCoinShopItem {
  id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  icon: string;
  tier_required: string | null;
  max_purchases: number | null;
  duration_hours: number | null;
  effect_json: string | null;
  canBuy: boolean;
  affordable: boolean;
  soldOut: boolean;
  purchaseCount: number;
}

export interface VibeCoinActiveEffect {
  id: string;
  item_name: string;
  item_category: string;
  effect_json: string | null;
  purchased_at: number;
  expires_at: number | null;
}

export interface VibeCoinEvent {
  id: string;
  name: string;
  description: string;
  eventType: string;
  config: { multiplier?: number; sources?: string[] };
  startsAt: number;
  endsAt: number;
}

// ── API Functions ──

export async function getBalance(): Promise<VibeCoinBalance> {
  const data = await fetchVibeCoin<{ balance: number; recent: VibeCoinTransaction[] }>('/api/vibecoin/balance');
  return { balance: data.balance, recentTransactions: data.recent || [] };
}

export async function getHistory(page = 1, direction?: 'credit' | 'debit'): Promise<{
  transactions: VibeCoinTransaction[];
  pagination: { page: number; limit: number; hasMore: boolean };
}> {
  const limit = 20;
  const offset = (page - 1) * limit;
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (direction) params.set('direction', direction);
  const data = await fetchVibeCoin<{ transactions: any[]; total: number }>(`/api/vibecoin/history?${params}`);
  const txs = (data.transactions || []).map((tx: any) => ({
    id: tx.id,
    type: tx.direction as 'credit' | 'debit',
    amount: tx.amount,
    source: tx.source,
    description: tx.description,
    balance_after: tx.balance_after,
    created_at: tx.created_at,
  }));
  return {
    transactions: txs,
    pagination: { page, limit, hasMore: offset + limit < (data.total || 0) },
  };
}

export async function getShop(): Promise<VibeCoinShopItem[]> {
  const data = await fetchVibeCoin<{ items: VibeCoinShopItem[]; balance: number }>('/api/vibecoin/shop');
  return data.items;
}

export async function purchaseItem(itemId: string, idempotencyKey: string): Promise<{
  purchaseId: string;
  newBalance: number;
  effect: any;
}> {
  return fetchVibeCoin('/api/vibecoin/spend', {
    method: 'POST',
    body: JSON.stringify({ item_id: itemId, idempotency_key: idempotencyKey }),
  });
}

export async function getActiveEffects(): Promise<VibeCoinActiveEffect[]> {
  const data = await fetchVibeCoin<{ effects: VibeCoinActiveEffect[] }>('/api/vibecoin/active-effects');
  return data.effects || [];
}

export async function getActiveEvents(): Promise<VibeCoinEvent[]> {
  const data = await fetchVibeCoin<{ events: VibeCoinEvent[] }>('/api/vibecoin/events/active');
  return data.events;
}
